import React, {Component} from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import {connect} from 'react-redux';
import {compose} from 'redux';
import SHLoader from 'common/loader';
import {SEARCH_TYPE, PROPTYPE_FACILITY_SUMMARY} from 'proptypes';
import WithSearch from './WithSearch';
import SearchLayoutDesktop from './layouts/desktop';
import SpotList from 'common/spot-list';
import SpotListSorter from 'common/search-sort-controls';
import SpotListSearchControls from './components/search-controls';
import SearchMap from 'common/search-results-map/search-map';
import SpotListSearchControlsCondensed from './components/search-controls-condensed';
import SpotListItemTransient from './components/spot-card-transient';
import {loadSpotsTransientV2} from 'store/spots/spots-actions-v2';
import {getSpotsV2} from 'store/spots/spots-selectors';
import {SEARCH_TRACKING_ACTIONS} from 'utils/search-tracking';
import TransientOfferSchema from 'seo/transient-offer-schema';
import SegmentUtils from 'utils/segment';
import {Modal, ModalContent} from '@spothero/ui-backlog';
import {Box} from '@spothero/ui';
import WithSortByPriceExperiment from 'pages/search-transient-event-monthly/WithSortByPriceExperiment';

const SpotDetails = loadable(() =>
    import('./components/spot-details-transient')
);
const EventList = loadable(() => import('./components/event-list'));

export class SearchDesktopDestination extends Component {
    static propTypes = {
        history: PropTypes.shape({
            location: PropTypes.object,
        }),
        sortBy: PropTypes.string,
        starts: PropTypes.string,
        ends: PropTypes.string,
        activeSearchTitle: PropTypes.string,
        canonicalUrl: PropTypes.string,
        city: PropTypes.object,
        spotsV2: PropTypes.arrayOf(PROPTYPE_FACILITY_SUMMARY),
        selectedSpotId: PropTypes.number,
        searchType: SEARCH_TYPE.isRequired,
        searchRequestId: PropTypes.string,
        showEventList: PropTypes.bool,
        findParkingNearMe: PropTypes.func,
        onToggleEventList: PropTypes.func.isRequired,
        onSpotDetailsHidden: PropTypes.func,
        onShowDetailsClick: PropTypes.func.isRequired,
        onChangeSort: PropTypes.func.isRequired,
        loadTransientSpots: PropTypes.func.isRequired,
        destination: PropTypes.object,
    };

    componentDidMount() {
        const {starts, ends} = this.props;

        if (starts && ends) {
            SegmentUtils.waitForSegmentThenLoad(5000, this._loadSpots, {
                searchTrackingAction: SEARCH_TRACKING_ACTIONS.SEARCH,
            });
        }
    }

    componentDidUpdate(prevProps) {
        const {starts, ends} = this.props;
        const {starts: prevStarts, ends: prevEnds} = prevProps;

        if (prevStarts !== starts || prevEnds !== ends) {
            this._loadSpots({
                searchTrackingAction: SEARCH_TRACKING_ACTIONS.DATE_CHANGED,
            });
        }
    }

    _onChangeSort = ({value}) => {
        this.props.onChangeSort(value);
    };

    _onModalClose = () => {
        const {destination, onToggleEventList} = this.props;
        const {title, id: destinationId} = destination;

        SegmentUtils.track({
            event: 'Find Non-Event Parking Clicked',
            properties: {
                /* eslint-disable camelcase */
                destination_name: title,
                destination_id: destinationId,
                /* eslint-enable camelcase */
            },
        });

        onToggleEventList();
    };

    _loadSpots = ({searchTrackingAction, mapCenter, mapBounds}) => {
        const {searchType, loadTransientSpots} = this.props;

        loadTransientSpots({
            searchTrackingAction,
            locationContext: searchType,
            mapCenter,
            mapBounds,
        });
    };

    render() {
        const {
            sortBy,
            starts,
            ends,
            activeSearchTitle,
            searchRequestId,
            canonicalUrl,
            city,
            spotsV2: spots,
            selectedSpotId,
            findParkingNearMe,
            onSpotDetailsHidden,
            showEventList,
            onToggleEventList,
            onShowDetailsClick,
        } = this.props;
        const numSpots = spots.length;
        const sortSelectItems = [
            {value: 'distance', label: 'Sort by Distance'},
            {value: 'price', label: 'Sort by Price'},
        ];

        const Map = (
            <SearchMap
                spots={spots}
                loadSpots={this._loadSpots}
                findParkingNearMe={findParkingNearMe}
                onShowDetailsClick={onShowDetailsClick}
            />
        );
        const Details = (
            <SpotDetails
                spotId={selectedSpotId}
                onCloseClick={onSpotDetailsHidden}
            />
        );
        const List = (
            <SpotList
                spots={spots}
                selectedSpotId={selectedSpotId}
                canonicalUrl={canonicalUrl}
                onShowDetailsClick={onShowDetailsClick}
                SpotListItem={SpotListItemTransient}
            />
        );
        const Sorter = (
            <SpotListSorter
                sortBy={sortBy}
                sortSelectItems={sortSelectItems}
                onChangeSort={this._onChangeSort}
            />
        );

        const Controls = (
            <SpotListSearchControls
                numSpots={numSpots}
                onToggleEventList={onToggleEventList}
            />
        );

        const ControlsCondensed = (
            <SpotListSearchControlsCondensed
                starts={starts}
                ends={ends}
                title={activeSearchTitle}
                parkingType="transient"
            />
        );

        return (
            <div className="Search">
                {searchRequestId ? (
                    <>
                        {spots.length > 0 && (
                            <TransientOfferSchema
                                spots={spots}
                                starts={starts}
                                ends={ends}
                                citySlug={city.slug}
                                canonicalUrl={canonicalUrl}
                            />
                        )}
                        <SearchLayoutDesktop
                            SearchMap={Map}
                            SpotDetails={Details}
                            SpotList={List}
                            SpotListSorter={Sorter}
                            SpotListSearchControls={Controls}
                            SpotListSearchControlsCondensed={ControlsCondensed}
                            spots={spots}
                            selectedSpotId={selectedSpotId}
                            showEventList={showEventList}
                        />
                    </>
                ) : (
                    <SHLoader />
                )}
                {typeof window !== 'undefined' && showEventList && (
                    <Modal
                        closeOnShimClick={false}
                        onCloseClick={onToggleEventList}
                        className="EventList-modal"
                        data-testid="EventList-modal"
                    >
                        <Box
                            height={713}
                            margin="0"
                            padding="0"
                            sx={{
                                '.Modal-content': {
                                    height: '100%',
                                    padding: '0',
                                    margin: '0',
                                },
                                '.Modal-content-wrapper': {
                                    height: '100%',
                                },
                                '.EventListItem-bottom': {
                                    p: {color: 'gray.600'},
                                },
                            }}
                        >
                            <ModalContent>
                                <EventList
                                    onViewTransientClick={onToggleEventList}
                                />
                            </ModalContent>
                        </Box>
                    </Modal>
                )}
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        city: {data: city},
        destination: {data: destination},
        search: {
            data: {
                breadcrumbs: {
                    activeBreadcrumb: {label: activeSearchTitle},
                },
                searchType,
                sortBy,
            },
        },
        searchRequest: {id: searchRequestId},
    } = state;

    return {
        city,
        searchType,
        destination,
        activeSearchTitle,
        searchRequestId,
        spotsV2: getSpotsV2(state),
        sortBy,
    };
};

const mapDispatchToProps = {
    loadTransientSpots: loadSpotsTransientV2,
};

const enhance = compose(
    WithSearch,
    WithSortByPriceExperiment,
    connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(SearchDesktopDestination);
